import React, { useMemo, useState } from 'react';
import Modal from 'react-modal';
import { resolveMediaUrl } from '../utils/api';
import { niceNameFromUrl } from '../utils/attachments';

Modal.setAppElement('#root');

const typeIcon = {
    image: '🖼️',
    video: '🎬',
    audio: '🎵',
    file: '📎',
};

const AttachmentList = ({ attachments = [], onRemove, maxVisible = 3, title = 'Attachments' }) => {
    const [previewItem, setPreviewItem] = useState(null);

    const visible = useMemo(() => attachments.slice(0, maxVisible), [attachments, maxVisible]);
    const hiddenCount = Math.max(attachments.length - visible.length, 0);

    const renderThumb = (item) => {
        const resolved = resolveMediaUrl(item.url);
        if (item.kind === 'image') {
            return <img src={resolved} alt="" className="attachment-thumb-media" />;
        }
        if (item.kind === 'video') {
            return <video src={resolved} preload="metadata" muted playsInline className="attachment-thumb-media" />;
        }
        return <span className="attachment-icon">{typeIcon[item.kind] || typeIcon.file}</span>;
    };

    return (
        <div className="attachment-list">
            <div className="attachment-list-header">
                <span className="attachment-title">{title}</span>
                <span className="attachment-counter">{attachments.length}</span>
            </div>

            {attachments.length === 0 && <p className="text-muted">Keine Attachments</p>}

            {visible.map((item, index) => (
                <div key={item.id || `${item.url}-${index}`} className="attachment-row">
                    <div className="attachment-thumb">{renderThumb(item)}</div>
                    <div className="attachment-meta">
                        <div className="attachment-label" title={item.title || item.displayName || item.originalName || item.url}>
                            {item.title || item.displayName || item.originalName || niceNameFromUrl(item.url)}
                        </div>
                        <div className="attachment-url text-muted" title={item.url}>
                            {item.url}
                        </div>
                    </div>
                    <div className="attachment-actions">
                        <button className="btn btn-sm btn-surface" type="button" onClick={() => setPreviewItem(item)}>
                            Preview
                        </button>
                        {onRemove && (
                            <button className="btn btn-sm btn-ghost" type="button" onClick={() => onRemove(index)}>
                                Entfernen
                            </button>
                        )}
                    </div>
                </div>
            ))}

            {hiddenCount > 0 && (
                <div className="text-muted small">+{hiddenCount} mehr</div>
            )}

            <Modal
                isOpen={!!previewItem}
                onRequestClose={() => setPreviewItem(null)}
                className="modal-content"
                overlayClassName="modal-overlay"
                contentLabel="Attachment Preview"
            >
                {previewItem && (
                    <div className="attachment-preview">
                        <h4>{previewItem.title || previewItem.displayName || previewItem.originalName || 'Preview'}</h4>
                        <div className="preview-body">
                            {previewItem.kind === 'image' && (
                                <img
                                    src={resolveMediaUrl(previewItem.url)}
                                    alt={previewItem.displayName || previewItem.originalName || 'Attachment'}
                                    style={{ maxWidth: '100%' }}
                                />
                            )}
                            {previewItem.kind === 'video' && (
                                <video
                                    src={resolveMediaUrl(previewItem.url)}
                                    controls
                                    style={{ width: '100%' }}
                                    preload="metadata"
                                />
                            )}
                            {previewItem.kind === 'audio' && (
                                <audio src={resolveMediaUrl(previewItem.url)} controls style={{ width: '100%' }} />
                            )}
                            {previewItem.kind === 'file' && (
                                <a href={resolveMediaUrl(previewItem.url)} target="_blank" rel="noreferrer">
                                    {previewItem.url}
                                </a>
                            )}
                        </div>
                        <div className="button-row" style={{ marginTop: '12px' }}>
                            <button
                                className="btn btn-primary"
                                onClick={() => window.open(resolveMediaUrl(previewItem.url), '_blank', 'noopener')}
                            >
                                In neuem Tab öffnen
                            </button>
                            <button className="btn btn-ghost" onClick={() => setPreviewItem(null)}>
                                Schließen
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AttachmentList;
